import React from 'react';
import RACES from '../../data/races';
import STATS from '../../data/stats';
import SKILLS from '../../data/skills';
import STAR_SIGNS from '../../data/star_signs';
import MAGIC_SCHOOLS from '../../data/magic_schools';
import { ARMOR_PIECES, ARMOR_QUALITIES, SHIELD_TYPES } from '../../data/armor';
import { btnStyle } from '../../styles/theme';
import SectionHeader from '../ui/SectionHeader';
import WeaponEditor from '../ui/WeaponEditor';
import EquipmentItem from '../ui/EquipmentItem';
import GearItem from '../ui/GearItem';


function CharacterDetails({ character, setCharacter, onSave }) {
  const race = RACES.find(r => r.id === character.race);
  const starSign = STAR_SIGNS.find(s => s.id === character.starSign);

  // Stat modifiers
  const modifiers = {
    might: Math.floor(character.stats.might / 10),
    speed: Math.floor(character.stats.speed / 10),
    intellect: Math.floor(character.stats.intellect / 10),
  };
  const magicPower = Math.floor(character.stats.magic / 5);

  function handleSave() {
    if (onSave && onSave()) {
      alert('Character saved successfully!');
    }
  }

  return (
    <div style={{ position: "relative", zIndex: 1, maxWidth: 820, margin: "0 auto", padding: "0 16px 40px" }}>
      {/* Character Header */}
      <div style={{ textAlign: "center", background: "linear-gradient(180deg, rgba(45,35,20,0.6), rgba(20,16,10,0.4))", border: "1px solid rgba(201,168,76,0.3)", borderRadius: "10px", padding: "20px", marginBottom: 20 }}>
        <div style={{ fontFamily: "'Cinzel Decorative', cursive", fontSize: "clamp(22px, 5vw, 32px)", color: "#c9a84c", textShadow: "0 0 12px rgba(201,168,76,0.3)" }}>{character.name}</div>
        <div style={{ fontFamily: "'Cinzel', serif", fontSize: "13px", color: "#8a7e5e", marginTop: 4 }}>{race.name} · {starSign?.name}</div>
        <button onClick={handleSave} style={{ ...btnStyle("#16a085", "rgba(22,160,133,0.15)"), padding: "8px 24px", marginTop: 12 }}>
          💾 Save Character
        </button>
      </div>

      {/* Stats Display */}
      <SectionHeader>Statistics</SectionHeader>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px", marginBottom: 24 }}>
        {STATS.map(stat => {
          const current = character.stats[stat.id];
          const mod = stat.id !== "magic" ? modifiers[stat.id] : null;
          return (
            <div key={stat.id} style={{ background: "rgba(20,16,10,0.6)", border: `1px solid ${stat.color}44`, borderRadius: 8, padding: "12px 8px", textAlign: "center" }}>
              <div style={{ fontSize: "9px", color: stat.color, fontFamily: "'Cinzel', serif", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>{stat.label}</div>
              <div style={{ fontFamily: "'Cinzel', serif", fontSize: "28px", color: stat.color }}>{current}</div>
              {mod !== null && <div style={{ fontSize: "10px", color: "#6b6048" }}>Modifier: +{mod}</div>}
              {stat.id === "magic" && <div style={{ fontSize: "10px", color: "#6b6048" }}>Power: {magicPower} dice</div>}
            </div>
          );
        })}
      </div>

      {/* Skills Display */}
      <SectionHeader>Skills</SectionHeader>
      <div style={{ marginBottom: 24 }}>
        {["might","speed","intellect"].map(statId => {
          const stat = STATS.find(s => s.id === statId);
          const statMod = modifiers[statId];
          const hasSkills = SKILLS[statId].some(skill => (character.skills[skill.id] || 0) > 0);
          if (!hasSkills) return null;

          return (
            <div key={statId} style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: stat.color }} />
                <span style={{ fontFamily: "'Cinzel', serif", fontSize: "12px", color: stat.color }}>{stat.label} Skills</span>
                <span style={{ fontSize: "9px", color: "#6b6048" }}>(+{statMod} from stat)</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 6 }}>
                {SKILLS[statId].map(skill => {
                  const lvl = character.skills[skill.id] || 0;
                  if (lvl === 0) return null;
                  return (
                    <div key={skill.id} style={{ display: "flex", justifyContent: "space-between", background: "rgba(20,16,10,0.4)", borderRadius: 4, padding: "4px 10px", border: "1px solid rgba(201,168,76,0.08)" }}>
                      <span style={{ fontSize: "12px", color: "#c9a84c88" }}>{skill.name}</span>
                      <span style={{ fontSize: "11px", color: "#8a7e5e" }}>Lvl {lvl}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Magic Schools Display */}
      <SectionHeader>Magic Schools</SectionHeader>
      <div style={{ marginBottom: 24 }}>
        {(() => {
          const allMagics = [...MAGIC_SCHOOLS.simple, ...MAGIC_SCHOOLS.standard, ...MAGIC_SCHOOLS.great, ...MAGIC_SCHOOLS.master];
          const learnedMagics = allMagics.filter(m => (character.magics?.[m.id] || 0) > 0);

          if (learnedMagics.length === 0) {
            return <div style={{ fontSize: "12px", color: "#6b6048", fontStyle: "italic", padding: "10px 0" }}>No magic schools learned yet</div>;
          }

          return (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8 }}>
              {learnedMagics.map(magic => {
                const lvl = character.magics[magic.id];
                const tierColor = magic.tier === "Simple" ? "#16a085" : magic.tier === "Standard" ? "#27ae60" : magic.tier === "Great" ? "#2980b9" : "#8e44ad";
                return (
                  <div key={magic.id} style={{ background: "rgba(20,16,10,0.5)", border: `1px solid ${tierColor}33`, borderRadius: 6, padding: "8px 12px" }}>
                    <div style={{ fontSize: "13px", color: tierColor, marginBottom: 2 }}>{magic.name}</div>
                    <div style={{ fontSize: "10px", color: "#6b6048", marginBottom: 4 }}>{magic.tier} Tier · {magic.powerDice}</div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: "11px", color: "#8a7e5e" }}>Level {lvl}</span>
                      <span style={{ fontSize: "9px", color: "#6b6048" }}>Cost: {magic.useCost} Magic</span>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* Weapons */}
      <SectionHeader>Weapons</SectionHeader>
      <div style={{ marginBottom: 24 }}>
        {(character.weapons || []).map((weapon, idx) => (
          <WeaponEditor key={weapon.id} weapon={weapon} character={character} setCharacter={setCharacter} idx={idx} />
        ))}
        <button onClick={() => {
          const newWeapon = {
            id: Date.now(),
            name: "New Weapon",
            type: "small_melee",
            quality: "simple",
            ability: "none",
            rangedSubtype: null,
          };
          setCharacter({ ...character, weapons: [...(character.weapons || []), newWeapon] });
        }} style={{ ...btnStyle("#c9a84c", "rgba(201,168,76,0.1)"), padding: "8px 16px", width: "100%" }}>
          + Add Weapon
        </button>
      </div>

      {/* Alchemy */}
      <SectionHeader>Alchemy</SectionHeader>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8, marginBottom: 24 }}>
        {["auxiliary", "organic", "conversion", "augmentation"].map(subset => {
          const cls = character.alchemy?.[subset] || 1;
          return (
            <div key={subset} style={{ background: "rgba(20,16,10,0.5)", border: "1px solid rgba(142,68,173,0.2)", borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
              <div style={{ fontFamily: "'Cinzel', serif", fontSize: "13px", color: "#c39bd3", textTransform: "capitalize" }}>{subset}</div>
              <div style={{ fontFamily: "'Cinzel', serif", fontSize: "18px", color: "#8e44ad", marginTop: 4 }}>Class {cls}</div>
            </div>
          );
        })}
      </div>

      {/* Armor */}
      <SectionHeader>Armor & Shield</SectionHeader>
      <div style={{ marginBottom: 24 }}>
        <div style={{ background: "rgba(20,16,10,0.6)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 8, padding: "14px 16px", marginBottom: 10 }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontFamily: "'Cinzel', serif", fontSize: "11px", color: "#c9a84c", letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Armor Pieces</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
              {ARMOR_PIECES.map(piece => {
                const armorValue = character.armor?.[piece.id] || "none";
                const qualities = ARMOR_QUALITIES[piece.category];
                const quality = qualities.find(q => q.id === armorValue);
                return (
                  <div key={piece.id}>
                    <label style={{ fontSize: "9px", color: "#6b6048", letterSpacing: 1, textTransform: "uppercase", display: "block", marginBottom: 4 }}>
                      {piece.name} ({piece.category})
                    </label>
                    <select
                      value={armorValue}
                      onChange={(e) => setCharacter({ ...character, armor: { ...(character.armor || {}), [piece.id]: e.target.value } })}
                      style={{ width: "100%", background: "rgba(0,0,0,0.4)", border: "1px solid rgba(201,168,76,0.3)", borderRadius: 4, padding: "6px 8px", color: "#e8dcc8", fontSize: "11px" }}>
                      {qualities.map(q => <option key={q.id} value={q.id}>{q.name}</option>)}
                    </select>
                    {quality && quality.id !== "none" && (
                      <div style={{ fontSize: "9px", color: "#8a7e5e", marginTop: 2 }}>
                        AR: {quality.ar} | MAR: {quality.mar} | Det: {quality.detriment}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div style={{ fontFamily: "'Cinzel', serif", fontSize: "11px", color: "#c9a84c", letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Shield</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
              <div>
                <select
                  value={character.shield || "none"}
                  onChange={(e) => setCharacter({ ...character, shield: e.target.value })}
                  style={{ width: "100%", background: "rgba(0,0,0,0.4)", border: "1px solid rgba(201,168,76,0.3)", borderRadius: 4, padding: "6px 8px", color: "#e8dcc8", fontSize: "12px" }}>
                  {SHIELD_TYPES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {(() => {
                  const shield = SHIELD_TYPES.find(s => s.id === (character.shield || "none"));
                  return shield && shield.id !== "none" ? (
                    <div style={{ fontSize: "9px", color: "#8a7e5e", marginTop: 2 }}>
                      AR: {shield.ar} | Block: +{shield.blockBonus} | Det: {shield.detriment}
                    </div>
                  ) : null;
                })()}
              </div>
            </div>
          </div>

          {/* Totals */}
          <div style={{ marginTop: 12, padding: "10px 12px", background: "rgba(201,168,76,0.05)", borderRadius: 4, border: "1px solid rgba(201,168,76,0.2)" }}>
            <div style={{ fontFamily: "'Cinzel', serif", fontSize: "10px", color: "#c9a84c", letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Total Protection</div>
            {(() => {
              let totalAR = 0;
              let totalMAR = 0;
              let totalDetriment = 0;

              // Sum armor pieces
              ARMOR_PIECES.forEach(piece => {
                const armorValue = character.armor?.[piece.id] || "none";
                const qualities = ARMOR_QUALITIES[piece.category];
                const quality = qualities.find(q => q.id === armorValue);
                if (quality) {
                  totalAR += quality.ar;
                  totalMAR += quality.mar;
                  totalDetriment += quality.detriment;
                }
              });

              // Add shield
              const shield = SHIELD_TYPES.find(s => s.id === (character.shield || "none"));
              if (shield) {
                totalAR += shield.ar;
                totalDetriment += shield.detriment;
              }

              return (
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: "12px", color: "#a08d4e" }}>
                  <div><strong style={{ color: "#c9a84c" }}>Total AR:</strong> {totalAR}</div>
                  <div><strong style={{ color: "#c9a84c" }}>Total MAR:</strong> {totalMAR}</div>
                  <div><strong style={{ color: "#c9a84c" }}>Total Detriment:</strong> -{totalDetriment}</div>
                  <div style={{ flex: "1 1 100%", fontSize: "10px", color: "#6b6048" }}>
                    Heavy armor incurs -1 per piece to Speed Skills and Magic Schools
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Equipment */}
      <SectionHeader>Equipment</SectionHeader>
      <div style={{ marginBottom: 24 }}>
        <p style={{ fontSize: "12px", color: "#8a7e5e", marginBottom: 12, lineHeight: 1.5 }}>
          Quick-access items like potions, ammunition, and tools. These can be accessed without using an Action.
        </p>
        {(character.equipment || []).map((item, idx) => (
          <EquipmentItem key={item.id} item={item} character={character} setCharacter={setCharacter} idx={idx} />
        ))}
        <button onClick={() => {
          const newItem = {
            id: Date.now(),
            name: "New Item",
            quantity: 1,
          };
          setCharacter({ ...character, equipment: [...(character.equipment || []), newItem] });
        }} style={{ ...btnStyle("#c9a84c", "rgba(201,168,76,0.1)"), padding: "8px 16px", width: "100%" }}>
          + Add Equipment
        </button>
      </div>

      {/* Gear */}
      <SectionHeader>Gear</SectionHeader>
      <div style={{ marginBottom: 24 }}>
        <p style={{ fontSize: "12px", color: "#8a7e5e", marginBottom: 12, lineHeight: 1.5 }}>
          Special items that provide bonuses or effects (enchanted items, magical artifacts, etc.).
        </p>
        {(character.gear || []).map((item, idx) => (
          <GearItem key={item.id} item={item} character={character} setCharacter={setCharacter} idx={idx} />
        ))}
        <button onClick={() => {
          const newItem = {
            id: Date.now(),
            name: "New Gear",
            description: "",
          };
          setCharacter({ ...character, gear: [...(character.gear || []), newItem] });
        }} style={{ ...btnStyle("#c9a84c", "rgba(201,168,76,0.1)"), padding: "8px 16px", width: "100%" }}>
          + Add Gear
        </button>
      </div>
    </div>
  );
}

export default CharacterDetails
